import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AnimatedCard from "../../../../components/AnimatedCard/AnimatedCard";
import PageContainer from "../../../../components/PageContainer/PageContainer";
import StepIndicator from "../../../../components/StepIndicator/StepIndicator";
import BackButton from "../../../../components/BackButton";
import {
  listDocumentTemplates,
  downloadDocumentTemplate,
  DocumentTemplate,
} from "../../../../services/documentTemplatesApiService";
import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import DocumentViewer from "../../../../components/DocumentViewer/DocumentViewer";
import SignatureCanvas from "../../../../components/SignatureCanvas/SignatureCanvas";
import { useSelector } from "react-redux";
import { RootState } from "../../../../redux/store";
import { Alert } from "@repo/ui";
import { getCookie } from "../../../../utils/cookieUtils";
// Import connected account services.
import { getConnectedAccount } from "../../../../services/stripeApiService";
import { ConnectAccountResponse } from "../../../../services/stripeAccountTypes";
// Import withdraw service.
import {
  getWithdrawalById,
  WithdrawResponse,
  setDonationReceiptID,
  getManualProfile,
  ManualProfileResponse,
} from "../../../../services/withdrawApiService";
// Import documents API functions:
import {
  createDocument,
  uploadDocument,
  verifyDocument,
} from "../../../../services/documentsApiService";

// For date formatting.
const formatCurrentDate = () =>
  new Date().toLocaleDateString("es-MX", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const DonationReceiptStep2: React.FC = () => {
  const { withdrawId } = useParams<{ withdrawId: string }>();
  const navigate = useNavigate();

  // Retrieve user's email and id from Redux.
  const user = useSelector((state: RootState) => state.auth.user);
  const userEmail = user?.email || "";
  const userId = user?.id;

  // New state for the withdrawal data.
  const [withdrawalData, setWithdrawalData] = useState<WithdrawResponse | null>(
    null
  );

  // Document URL and template metadata.
  const [documentUrl, setDocumentUrl] = useState<string | null>(null);
  const [templateInfo, setTemplateInfo] = useState<DocumentTemplate | null>(
    null
  );
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Signature state.
  const [, setSignature] = useState<string | null>(null);
  const [, setIsProcessing] = useState<boolean>(false);
  const [userAlert, setUserAlert] = useState<{
    message: string;
    level: "info" | "warn" | "error" | "success";
  } | null>(null);

  // Preview URL for the unsigned document with dynamic text.
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // State for the connected account retrieved from Stripe.
  const [connectAccount, setConnectAccount] =
    useState<ConnectAccountResponse | null>(null);
  const [manualProfile, setManualProfile] = useState<ManualProfileResponse | null>(null);

  // Load withdrawal data using the withdrawId.
  useEffect(() => {
    if (withdrawId) {
      getWithdrawalById(withdrawId)
        .then((data) => {
          const jwt = getCookie("authToken");
          if (!jwt) {
            const current = encodeURIComponent(window.location.href);
            window.location.href = `${import.meta.env.VITE_LANDING_APP_URL}/auth?redirect=${current}`;
            return;
          }
          setWithdrawalData(data);
        })
        .catch((err) => {
          console.error("Error retrieving withdrawal data", err);
        });
    }
  }, [withdrawId]);

  // Load document template filtered by name "donation-receipt" and retrieve its download URL.
  useEffect(() => {
    listDocumentTemplates(1, undefined, "donation-receipt")
      .then((response) => {
        if (
          response.document_templates &&
          response.document_templates.length > 0
        ) {
          const template = response.document_templates[0];
          setTemplateInfo(template);
          return downloadDocumentTemplate(template.id);
        } else {
          throw new Error("No se encontró la plantilla de recibo de donación.");
        }
      })
      .then((downloadResponse) => {
        setDocumentUrl(downloadResponse.download_url);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error al cargar la plantilla de documento", err);
        setError("Error al cargar la plantilla de documento.");
        setLoading(false);
      });
  }, []);

  // Retrieve connected account information using user id.
  useEffect(() => {
    if (userId) {
      getConnectedAccount(userId)
        .then((data) => {
          setConnectAccount(data);
        })
        .catch((err) => {
          console.error("Error retrieving connected account", err);
        });
    }
  }, [userId]);

  // Retrieve bank account information using user id.
  // Retrieve manual profile using withdrawal ID.
  useEffect(() => {
    if (withdrawId) {
      getManualProfile(withdrawId)
        .then((profile) => setManualProfile(profile))
        .catch(() => setManualProfile(null));
    }
  }, [withdrawId]);

  // Process document: embed signature (if provided) and replace text fields.
  const processDocument = async (sig?: string): Promise<Uint8Array> => {
    try {
      if (!documentUrl) throw new Error("Document URL no definida.");
      if (!templateInfo)
        throw new Error("Información de plantilla no disponible.");

      const SIGNATURE_Y_OFFSET = 30;
      const TEXT_Y_OFFSET = 30;

      const response = await fetch(documentUrl);
      const pdfBytes = await response.arrayBuffer();
      const pdfDoc = await PDFDocument.load(pdfBytes);
      const pages = pdfDoc.getPages();

      if (sig) {
        const signatureData = sig.split(",")[1];
        const binaryString = atob(signatureData);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const signatureImage = await pdfDoc.embedPng(bytes);
        const sigLoc = templateInfo.signature_locations["signature-1"];
        const targetPage = pages[sigLoc.page - 1];
        const pageHeight = targetPage.getHeight();
        const newY =
          pageHeight -
          sigLoc.position.y -
          sigLoc.position.height +
          SIGNATURE_Y_OFFSET;
        targetPage.drawImage(signatureImage, {
          x: sigLoc.position.x,
          y: newY,
          width: sigLoc.position.width,
          height: sigLoc.position.height,
        });
      }

      const helveticaFont = await pdfDoc.embedFont(StandardFonts.Helvetica);
      const textInterps = templateInfo.text_interpolations;

      for (const key in textInterps) {
        if (Object.prototype.hasOwnProperty.call(textInterps, key)) {
          const field = textInterps[key];
          let textValue: string;
          switch (key) {
            case "account-number":
              textValue =
                manualProfile?.clabe || manualProfile?.account || "--NA--";
              break;
            case "address":
              if (manualProfile) {
                textValue = `${manualProfile.addressLine1 || ""} ${
                  manualProfile.addressLine2 || ""
                }, ${manualProfile.city || ""}, ${manualProfile.state || ""}`;
              } else if (
                connectAccount &&
                connectAccount.stripe_account.individual.address
              ) {
                const addr = connectAccount.stripe_account.individual.address;
                textValue = `${addr.line1 || ""}, ${addr.city || ""}, ${
                  addr.state || ""
                }`;
              } else {
                textValue = "--NA--";
              }
              break;
            case "ammount":
              textValue = withdrawalData
                ? withdrawalData.amount.toLocaleString("es-MX", {
                    style: "currency",
                    currency: withdrawalData.currency || "MXN",
                  })
                : "--NA--";
              break;
            case "bank":
              textValue = manualProfile?.bank || "--NA--";
              break;
            case "clabe":
              if (manualProfile?.clabe) {
                const last4 = manualProfile.clabe.slice(-4);
                textValue = `****${last4}`;
              } else {
                textValue = "--NA--";
              }
              break;
            case "date":
              textValue = formatCurrentDate();
              break;
            case "email":
              textValue = userEmail;
              break;
            case "full-name":
              if (manualProfile?.holderName) {
                textValue = manualProfile.holderName;
              } else if (
                connectAccount &&
                connectAccount.stripe_account.individual
              ) {
                const indiv = connectAccount.stripe_account.individual;
                textValue = `${indiv.first_name || ""} ${indiv.last_name || ""}`;
              } else {
                textValue = "--NA--";
              }
              break;
            case "official-id-document":
              textValue = "-- Verificado por Stripe --";
              break;
            case "place":
              textValue = "México";
              break;
            case "sign-name":
              if (connectAccount && connectAccount.stripe_account.individual) {
                const indiv = connectAccount.stripe_account.individual;
                textValue = `${indiv.first_name || ""} ${indiv.last_name || ""}`;
              } else {
                textValue = "--NA--";
              }
              break;
            case "transfer-date":
              textValue =
                "5 días hábiles a partir de la fecha de este documento";
              break;
            default:
              textValue = `-------${key}--------`;
          }
          const targetPage = pages[field.page - 1];
          const pageHeight = targetPage.getHeight();
          const newY = pageHeight - field.y - field.size + TEXT_Y_OFFSET;
          let textColor = rgb(0, 0, 0);
          if (field.color) {
            const hex = field.color.replace("#", "");
            const r = parseInt(hex.substring(0, 2), 16) / 255;
            const g = parseInt(hex.substring(2, 4), 16) / 255;
            const b = parseInt(hex.substring(4, 6), 16) / 255;
            textColor = rgb(r, g, b);
          }
          targetPage.drawText(textValue, {
            x: field.x,
            y: newY,
            size: field.size,
            font: helveticaFont,
            color: textColor,
          });
        }
      }
      return await pdfDoc.save();
    } catch (error) {
      console.error("Error processing document:", error);
      throw error;
    }
  };

  // Generate preview (unsigned) when documentUrl and templateInfo are ready.
  useEffect(() => {
    if (documentUrl && templateInfo) {
      processDocument()
        .then((pdfBytes) => {
          const blob = new Blob([Uint8Array.from(pdfBytes)], {
            type: "application/pdf",
          });
          const objectUrl = URL.createObjectURL(blob);
          setPreviewUrl(objectUrl);
        })
        .catch((err) => {
          console.error("Error processing preview document:", err);
        });
    }
  }, [
    documentUrl,
    templateInfo,
    connectAccount,
    withdrawalData,
    manualProfile,
  ]);

  // Finalize document when the signature is provided:
  // After processing, upload the document to the documents API and then call setDonationReceiptID.
  const handleFinalizeSignature = async (sigData: string) => {
    if (!sigData) {
      setUserAlert({
        message: "Por favor, firma el documento antes de continuar.",
        level: "error",
      });
      return;
    }
    setIsProcessing(true);
    try {
      // Process final signed PDF.
      const signedPdfBytes = await processDocument(sigData);
      const pdfBlob = new Blob([Uint8Array.from(signedPdfBytes)], {
        type: "application/pdf",
      });
      const file = new File([pdfBlob], `${withdrawId}_signed_receipt.pdf`, {
        type: "application/pdf",
      });
      // Create document using the documents API.
      const createdDoc = await createDocument({
        name: `${withdrawId}_signed_receipt`,
      });
      // Upload the file to the provided presigned URL.
      await uploadDocument(createdDoc.upload_url, createdDoc.upload_fields, file);
      // Verify the document using its ID.
      await verifyDocument(createdDoc.id);
      // Set the donation receipt ID in the withdrawal record.
      await setDonationReceiptID({
        donationReceiptID: createdDoc.id,
        withdrawID: withdrawId as string,
      });
      // Navigate to the next step.
      navigate(`/my-table/sign-donation-receipt/${withdrawId}/step3`);
    } catch (error) {
      setUserAlert({
        message:
          "Error al procesar y subir el documento firmado. Por favor, inténtalo de nuevo.",
        level: "error",
      });
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return <div>Cargando documento...</div>;
  }

  if (error || !documentUrl) {
    return <div>{error || "Error al cargar el documento."}</div>;
  }

  return (
    <PageContainer>
      <div className="mt-16"></div>
      <StepIndicator
        steps={["Revisión", "Firma", "Confirmación"]}
        currentStep={2}
        progressPercentage={66}
      />
      <AnimatedCard className="bg-white max-w-lg w-full">
        <BackButton
          text="Regresar"
          onClick={() =>
            navigate(`/my-table/sign-donation-receipt/${withdrawId}`)
          }
        />
        <h2 className="text-2xl font-bold mb-4 text-primary">
          Firma tu recibo de donación
        </h2>
        <p className="text-gray-600 mb-4">
          Revisa el documento con tu información antes de proceder a firmarlo
          con tu firma digital.
        </p>

        {/* Document Preview */}
        <div className="mb-4 border rounded shadow">
          {previewUrl ? (
            <DocumentViewer documentUrl={previewUrl} />
          ) : (
            <div>Cargando vista previa...</div>
          )}
        </div>

        {/* Signature Canvas */}
        <div className="mb-4">
          <h3 className="text-xl font-semibold mb-2">Tu firma</h3>
          <SignatureCanvas
            onSave={(sig) => setSignature(sig)}
            onComplete={handleFinalizeSignature}
          />
        </div>

        {userAlert && (
          <div className="mb-4">
            <Alert message={userAlert.message} level={userAlert.level} />
          </div>
        )}
      </AnimatedCard>
    </PageContainer>
  );
};

export default DonationReceiptStep2;
